import { ActorRefFrom, assign, emit, enqueueActions, setup } from "xstate";
import { BundleFormSchema, VariantPicksSchema } from "../models";
import { change, ChangeStateSchema, createChangeState } from "@greeter/changes";
import { assert } from "../assert";
import {
  BundleId,
  BundleIdSchema,
  BundleLineIdSchema,
  BundleLineVariantIdSchema,
  ImageAssetSchema,
  ProductIdSchema,
  QuantitySchema,
  SpecialPricePeriod,
  SpecialPricePeriodsSchema,
} from "@greeter/core";
import * as z from "zod";
import { JsonMapSchema } from "@greeter/json";
import clamp from "lodash/clamp";
import * as uuid from "uuid";
import { isNotType, isType } from "@greeter/util";
import { bundleSumTotalPrice } from "../BundlePicker/BundlePickerV3/util";
import { Log } from "@greeter/log";
import { upsert } from "@greeter/map";
import { unwrapData } from "@greeter/xstate-util";

function sumTotal(
  items: CartItems,
  opts?: { forDate?: Date; specialPricePeriods: Array<SpecialPricePeriod> }
) {
  let total = 0;

  for (const item of items.values()) {
    if (isType(item, "bundle", "simple")) {
      assert(typeof item.price === "number", "price is not of type 'number'");
      assert(
        typeof item.quantity === "number",
        "quantity is not of type 'number'"
      );
      assert(!isNaN(item.quantity));
      assert(!isNaN(item.price));
      total += item.quantity * item.price;
    } else if (isType(item, "bundle-form")) {
      const form = item.form;
      assert(typeof form.price === "number", "price is not of type 'number'");
      assert(
        typeof item.quantity === "number",
        "quantity is not of type 'number'"
      );
      assert(!isNaN(item.quantity));
      assert(!isNaN(form.price));
      total += bundleSumTotalPrice(item.form, opts) * item.quantity;
    }
  }

  Log.info(`[${sumTotal.name}] total`, total);
  return total;
}

function countQuantity(items: CartItems) {
  let total = 0;

  for (const item of items.values()) {
    total += item.quantity;
  }

  return total;
}

export const CartItemIdSchema = z.string();
export type CartItemId = z.infer<typeof CartItemIdSchema>;

export const CartItemBundleSchema = z.object({
  type: z.literal("bundle"),
  id: CartItemIdSchema,
  coverAsset: z.optional(z.nullable(ImageAssetSchema)),
  bundleId: BundleIdSchema,
  title: z.string(),
  picks: VariantPicksSchema,
  quantity: QuantitySchema,
  price: z.number(),
});
export type CartItemBundle = z.infer<typeof CartItemBundleSchema>;

export const CartItemSimpleSchema = z.object({
  type: z.literal("simple"),
  id: CartItemIdSchema,
  title: z.string(),
  coverAsset: ImageAssetSchema.optional(),
  productId: ProductIdSchema,
  quantity: QuantitySchema,
  price: z.number(),

  /**
   * This allows us to keep track of which bundle line and
   * bundle line variant this originates from, to convert back into a bundle
   * and to provide analytics for later use, when we need to determine
   * which are upsells etc.
   *
   * It also allows us to edit the bundle at a later point.
   */
  relation: z.optional(
    z.object({
      bundleLineId: BundleLineIdSchema,
      bundleLineVariantId: BundleLineVariantIdSchema,
    })
  ),
});
export type CartItemSimple = z.infer<typeof CartItemSimpleSchema>;

export const CartItemBundleFormSchema = z.object({
  type: z.literal("bundle-form"),
  id: CartItemIdSchema,
  form: BundleFormSchema,
  quantity: QuantitySchema,
});
export type CartItemBundleForm = z.infer<typeof CartItemBundleFormSchema>;

export const CartItemSchema = z.union([
  CartItemBundleSchema,
  CartItemBundleFormSchema,
  CartItemSimpleSchema,
]);
export type CartItem = z.infer<typeof CartItemSchema>;

export const CartItemsSchema = JsonMapSchema(CartItemIdSchema, CartItemSchema);
export type CartItems = z.infer<typeof CartItemsSchema>;

export const CartChangesSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("add"), data: CartItemSchema }),
  z.object({ type: z.literal("del"), data: CartItemIdSchema }),
  z.object({ type: z.literal("inc"), data: CartItemIdSchema }),
  z.object({ type: z.literal("dec"), data: CartItemIdSchema }),
  z.object({
    type: z.literal("setId"),
    data: z.object({ currentId: CartItemIdSchema, newId: CartItemIdSchema }),
  }),
]);
export type CartChanges =
  | { type: "add"; data: CartItem }
  | { type: "del"; data: CartItemId }
  | { type: "inc"; data: CartItemId }
  | { type: "dec"; data: CartItemId }
  | { type: "setId"; data: { currentId: CartItemId; newId: CartItemId } };

function applyCartChange(s: CartItems, c: CartChanges) {
  switch (c.type) {
    case "setId":
      if (c.data.currentId === c.data.newId) break;

      const existing = s.get(c.data.currentId);
      if (existing) {
        s.delete(existing.id);
        existing.id = c.data.newId;
        s.set(existing.id, existing);
      }
      break;

    case "add":
      s.set(c.data.id, c.data);
      break;

    case "del":
      s.delete(c.data);
      break;

    case "inc": {
      const cartItem = s.get(c.data);
      if (cartItem) {
        cartItem.quantity = clamp(
          cartItem.quantity + 1,
          0,
          Number.MAX_SAFE_INTEGER
        );
      }
      break;
    }

    case "dec": {
      const cartItem = s.get(c.data);
      if (cartItem) {
        cartItem.quantity = clamp(
          cartItem.quantity - 1,
          0,
          Number.MAX_SAFE_INTEGER
        );
      }
      break;
    }
  }
}

export class CartMap extends Map<CartItemId, CartItem> {
  constructor(args?: Map<CartItemId, CartItem>) {
    super(args);
  }
}

export const CartMapSchema = JsonMapSchema(
  CartItemIdSchema,
  CartItemSchema
).transform((o) => new CartMap(o));

export const CartContextSchema = z.object({
  state: ChangeStateSchema(CartMapSchema, CartChangesSchema),
  total: z.number().transform((n) => clamp(n, 0, Number.MAX_SAFE_INTEGER)),
  count: z.number().transform((n) => clamp(n, 0, Number.MAX_SAFE_INTEGER)),
  specialPricePeriods: SpecialPricePeriodsSchema,
  forDate: z
    .string()
    .or(z.date())
    .transform((s) => new Date(s)),
});
export type CartContext = z.infer<typeof CartContextSchema>;

export const cartMachine = setup({
  types: {} as {
    events:
      | { type: "change"; data: CartChanges }
      | { type: "reset" }
      | { type: "refreshIds" }
      | { type: "setSpecialPricePeriods"; data: Array<SpecialPricePeriod> }
      | { type: "setForDate"; data?: Date };
    emitted:
      | { type: "change"; data: CartMap }
      | { type: "added"; data: { id: string; name: string } };
    context: CartContext;
  },
  actions: {
    recalculateTotal: assign(({ context }) => ({
      total: sumTotal(context.state.value, {
        forDate: context.forDate,
        specialPricePeriods: context.specialPricePeriods,
      }),
    })),
  },
}).createMachine({
  initial: "open",

  context: {
    state: createChangeState(new CartMap()),
    total: 0,
    count: 0,
    specialPricePeriods: [],
    forDate: new Date(),
  },

  on: {
    setSpecialPricePeriods: {
      actions: [assign({ specialPricePeriods: unwrapData }), "recalculateTotal"],
    },

    setForDate: {
      actions: [assign({ forDate: unwrapData }), "recalculateTotal"],
    },
  },

  states: {
    open: {
      on: {
        reset: {
          actions: [
            assign(() => ({
              state: createChangeState(new CartMap(), []),
              total: 0,
              count: 0,
              specialPricePeriods: [],
              forDate: new Date(),
            })),
          ],
        },
        refreshIds: {
          actions: enqueueActions(({ context, enqueue }) => {
            for (const [, item] of context.state.value) {
              enqueue.raise({
                type: "change",
                data: {
                  type: "setId",
                  data: { currentId: item.id, newId: uuid.v7() },
                },
              });
            }
          }),
        },
        change: {
          actions: [
            assign(({ context, event }) => {
              Log.info("[cartMachine][change] state", context.state);
              const changed = change(
                context.state,
                applyCartChange,
                event.data
              );
              Log.info("[cartMachine][change] changed", changed);
              const count = countQuantity(context.state.value);
              return {
                state: changed,
                count,
              };
            }),
            "recalculateTotal",
            emit(({ event, context }) => {
              if (event.data.type === "add") {
                return {
                  type: "added",
                  data: {
                    id: event.data.data.id,
                    name: isType(event.data.data, "bundle-form")
                      ? event.data.data.form.title
                      : event.data.data.title,
                  },
                };
              }

              return {
                type: "change",
                data: new CartMap(context.state.value),
              };
            }),
          ],
        },
      },
    },
    locked: {},
  },
});

export type CartMachineRef = ActorRefFrom<typeof cartMachine>;
